import { resolveMediaPathForBrowser } from '../services/firebase/browserMedia';

export const BROWSER_IMAGE_LOAD_TIMEOUT_MS = 12_000;

export const loadBrowserImage = async (imagePath: string): Promise<HTMLImageElement> => {
  const trimmedImagePath = imagePath.trim();
  if (!trimmedImagePath) {
    throw new Error('Cannot load a blank image path.');
  }

  const resolvedPath = (await resolveMediaPathForBrowser(trimmedImagePath)).trim();
  if (!resolvedPath) {
    throw new Error(`Failed to resolve image path: ${trimmedImagePath}`);
  }

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    let settled = false;
    image.decoding = 'async';

    const cleanup = (timeoutId: number): void => {
      image.onload = null;
      image.onerror = null;
      window.clearTimeout(timeoutId);
    };

    const timeoutId = window.setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup(timeoutId);
      reject(new Error(`Timed out loading image: ${trimmedImagePath}`));
    }, BROWSER_IMAGE_LOAD_TIMEOUT_MS);

    image.onload = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup(timeoutId);
      resolve(image);
    };
    image.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup(timeoutId);
      reject(new Error(`Failed to load image: ${trimmedImagePath}`));
    };
    image.src = resolvedPath;
  });
};

export const loadBrowserImageDimensions = async (
  imagePath: string,
): Promise<{ width: number; height: number } | null> => {
  if (!imagePath.trim()) {
    return null;
  }

  try {
    const image = await loadBrowserImage(imagePath);
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  } catch {
    return null;
  }
};
